import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { DEFAULT_GC_TIME, DEFAULT_STALE_TIME } from '@/client/constants';
import { useClientStore } from '@/client/stores/client';
import { withErrorHandling } from '@/client/utils/errors';
import type { PromotionMetrics, PromotionMetricsParams } from '@/client/types/promotion';

const PROMOTION_METRICS = '/api/admin/promotions/metrics';

export const promotionMetricsKeys = {
  all: ['promotion-metrics'] as const,
  range: (params: PromotionMetricsParams) => [...promotionMetricsKeys.all, params] as const,
};

/** ff-api wraps payloads as `{ status, data }`. */
const unwrapEnvelope = <T,>(raw: any): T =>
  raw != null && typeof raw === 'object' && 'status' in raw && 'data' in raw ? raw.data : raw;

/** Missing collections default to empty so a partial payload still renders. */
const normalize = (raw: any, params: PromotionMetricsParams): PromotionMetrics => ({
  range: raw?.range ?? { from: params.from, to: params.to, bin: params.bin ?? 'month' },
  kpis: {
    revenue: raw?.kpis?.revenue ?? 0,
    orders: raw?.kpis?.orders ?? 0,
    aov: raw?.kpis?.aov ?? 0,
    buyers: raw?.kpis?.buyers ?? 0,
    refund_rate: raw?.kpis?.refund_rate ?? 0,
    revenue_growth: raw?.kpis?.revenue_growth ?? null,
    orders_growth: raw?.kpis?.orders_growth ?? null,
  },
  trend: (raw?.trend ?? []).map((point: any) => ({
    label: point?.label ?? '',
    period: point?.period ?? '',
    total: point?.total ?? 0,
    by_tier: point?.by_tier ?? {},
  })),
  by_category: raw?.by_category ?? [],
  by_tier: raw?.by_tier ?? [],
  top_buyers: raw?.top_buyers ?? [],
  recent_transactions: raw?.recent_transactions ?? [],
});

const useMetricsQueryFn = () => {
  const request = useClientStore((state) => state.request);

  return withErrorHandling(async (params: PromotionMetricsParams): Promise<PromotionMetrics> => {
    const search = Object.entries(params).reduce<Record<string, string>>((acc, [key, value]) => {
      if (value !== undefined && value !== null && value !== '') acc[key] = String(value);
      return acc;
    }, {});

    const raw = await request<any>(`${PROMOTION_METRICS}?${new URLSearchParams(search).toString()}`);
    return normalize(unwrapEnvelope<any>(raw), params);
  }, 'Failed to fetch promotion metrics');
};

/** Promotion revenue metrics for an inclusive `from`–`to` window. */
export const usePromotionMetricsQuery = (params: PromotionMetricsParams, enabled = true) => {
  const queryFn = useMetricsQueryFn();

  return useQuery({
    queryKey: promotionMetricsKeys.range(params),
    queryFn: () => queryFn(params),
    enabled: enabled && Boolean(params.from && params.to),
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
    placeholderData: keepPreviousData,
  });
};
